import React, { useEffect, useState, useCallback, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import notificationIcon from "../../images/notification.png";
import emptyImage from "../../images/empty.jpg";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import { common } from "../../util/common";
import {
  convertTo12HourPlusDateFormat,
  trimWord,
  formatBoldText,
  buildConversationPath,
} from "../../util/helperFunctions";
import bellNotifSound from "../../sounds/bell-notif.mp3";
import { useNavigate } from "react-router-dom";
import Websocket from "../../hooks/ws";

const timeAgo = (timestamp) => {
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (Number.isNaN(seconds) || seconds < 45) return "Just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return convertTo12HourPlusDateFormat(timestamp);
};

// Icon + colour tone per notification type (FontAwesome 4 classes).
const NOTIF_META = {
  mention: { icon: "fa-at", tone: "blue" },
  "thread mention": { icon: "fa-comments-o", tone: "blue" },
  reaction: { icon: "fa-heart", tone: "pink" },
  mute: { icon: "fa-bell-slash", tone: "gray" },
  unmute: { icon: "fa-bell", tone: "green" },
  removed: { icon: "fa-user-times", tone: "red" },
  "new form request": { icon: "fa-file-text-o", tone: "amber" },
  "approve form request": { icon: "fa-file-text-o", tone: "amber" },
  "deny form request": { icon: "fa-times-circle", tone: "red" },
  "approved form request": { icon: "fa-check-circle", tone: "green" },
  "denied form request": { icon: "fa-times-circle", tone: "red" },
  "task follow-up": { icon: "fa-tasks", tone: "blue" },
};

const getNotifMeta = (type) =>
  NOTIF_META[type?.toLowerCase?.() || ""] || { icon: "fa-bell", tone: "gray" };

const getInitials = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "?";

const dayLabel = (timestamp) => {
  const date = new Date(timestamp);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((startOfToday - date) / 86400000) + 1;
  if (Number.isNaN(diffDays) || date >= startOfToday) return "Today";
  if (diffDays <= 1) return "Yesterday";
  return "Earlier";
};

// Flat list -> [{kind:"header"}, {kind:"item"}, ...] with Today/Yesterday/Earlier.
const groupByDay = (list) => {
  const rows = [];
  let last = null;
  for (const notification of list) {
    const label = dayLabel(notification.createdAt);
    if (label !== last) {
      rows.push({ kind: "header", label });
      last = label;
    }
    rows.push({ kind: "item", notification });
  }
  return rows;
};

function NotificationBell({ addtlClass, userId, displayName }) {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const soundRef = useRef(null);
  const showNotificationsRef = useRef(false);
  const [filter, setFilter] = useState("all");
  const [loadError, setLoadError] = useState(false);
  const [popKey, setPopKey] = useState(0);

  const [showNotifications, setShowNotifications] = useState(false);

  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationList, setNotificationList] = useState([]);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [loadingFirstPage, setLoadingFirstPage] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const FINAL_RESOURCES_URL = `${process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL}/`;

  const mergeUniqueById = (prev, next) => {
    const map = new Map();
    for (const n of prev) map.set(n.id, n);
    for (const n of next) map.set(n.id, n);
    return Array.from(map.values());
  };

  const loadPage = useCallback(
    async (pageToLoad, mode = "append") => {
      // mode: "replace" | "append"
      const resp = await ApiService.users.getAllNotificationsv2(
        pageToLoad,
        limit,
      );
      const { success, notifications: notificationListResp } = resp || {};

      if (!success) {
        setLoadError(true);
        return false;
      }
      setLoadError(false);

      const {
        count,
        unread_count,
        notifications: incoming,
      } = notificationListResp;

      setTotalCount(count || 0);
      setNotificationCount(unread_count || 0);

      if (mode === "replace") {
        setNotificationList(incoming || []);
      } else {
        setNotificationList((prev) => mergeUniqueById(prev, incoming || []));
      }

      // hasMore based on count and page/limit
      const more = pageToLoad * limit < (count || 0);
      setHasMore(more);
      return true;
    },
    [limit],
  );

  // Cheap badge refresh: one COUNT query, never touches the list.
  const refreshCountsOnly = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const resp = await ApiService.users.getUnreadNotificationCount();
      if (!resp?.success) return;
      setNotificationCount(resp.unread_count || 0);
    } finally {
      // small delay prevents flicker
      setTimeout(() => setIsRefreshing(false), 400);
    }
  }, []);

  useEffect(() => {
    showNotificationsRef.current = showNotifications;
  }, [showNotifications]);

  // Badge is correct on page load, without opening the dropdown.
  useEffect(() => {
    refreshCountsOnly();
  }, [refreshCountsOnly]);

  // Real-time delivery over the shared socket.
  useEffect(() => {
    if (!userId) return;

    const ws = new Websocket();
    // Requests/support/profile layouts never connect; make sure a socket exists.
    if (!ws.getConnection()) ws.initConnection(userId, displayName);

    if (!soundRef.current) soundRef.current = new Audio(bellNotifSound);

    return ws.setupNotificationListener({
      onCreated: ({ unread_count }) => {
        setNotificationCount((prev) =>
          typeof unread_count === "number" ? unread_count : prev + 1,
        );
        setPopKey((k) => k + 1);
        soundRef.current?.play().catch(() => {});
        // Panel open: reload page 1 so the new item shows with full details.
        if (showNotificationsRef.current) {
          setPage(1);
          loadPage(1, "replace");
        }
      },
      onCount: ({ unread_count }) => {
        if (typeof unread_count === "number") setNotificationCount(unread_count);
      },
      // Covers events missed while the socket was down.
      onConnect: () => refreshCountsOnly(),
    });
  }, [userId, displayName, loadPage, refreshCountsOnly]);

  // Resync when the tab becomes visible again.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshCountsOnly();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshCountsOnly]);

  // When dropdown opens: fetch first page
  useEffect(() => {
    if (!showNotifications) return;

    (async () => {
      setLoadingFirstPage(true);
      setPage(1);
      await loadPage(1, "replace");
      setLoadingFirstPage(false);
    })();
  }, [showNotifications, loadPage]);

  // Slow fallback poll; the socket is the primary channel.
  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshCountsOnly();
    }, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [refreshCountsOnly]);

  const fetchMoreNotifications = async () => {
    if (isFetchingMore) return;
    setIsFetchingMore(true);

    try {
      const nextPage = page + 1;
      setPage(nextPage);
      await loadPage(nextPage, "append");
    } finally {
      setIsFetchingMore(false);
    }
  };

  const visibleList =
    filter === "unread"
      ? notificationList.filter((n) => !n.is_read)
      : notificationList;

  const groupedRows = groupByDay(visibleList);

  const activateNotification = (notification) => {
    if (notification.notification_type?.toLowerCase() === "removed") return;

    handleNotificationClick(
      notification.id,
      notification.conversation_id,
      notification.message_id,
      notification.reply_to_thread_message_id,
      notification.notification_type,
      notification.request_notification_form_request_id,
    );
  };

  const handleBellClick = () => {
    setShowNotifications((prev) => !prev);
  };

  useEffect(() => {
    if (!showNotifications) return;

    const handleOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showNotifications]);

  const handleReadDeleteClick = async (notification_id, action) => {
    const response = await ApiService.notifications.readDeleteNotification(
      { action },
      notification_id,
    );
    const { success } = response || {};
    if (!success) return showSwal(common.errors.UPDATE_NOTIFICATION_ERROR);

    // Update locally; the server also pushes the authoritative count.
    const target = notificationList.find((n) => n.id === notification_id);
    const wasUnread = target && !target.is_read;

    if (action === "delete") {
      setNotificationList((prev) =>
        prev.filter((n) => n.id !== notification_id),
      );
      setTotalCount((prev) => Math.max(0, prev - 1));
    }

    if (action === "read") {
      setNotificationList((prev) =>
        prev.map((n) =>
          n.id === notification_id ? { ...n, is_read: true } : n,
        ),
      );
    }

    if (wasUnread) setNotificationCount((prev) => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async () => {
    if (notificationCount === 0) return;
    const response = await ApiService.notifications.readAllNotifications();
    if (!response?.success) return showSwal(common.errors.UPDATE_NOTIFICATION_ERROR);

    setNotificationList((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setNotificationCount(0);
  };

  const handleNotificationClick = async (
    notification_id,
    conversation_id,
    message_id,
    reply_to_thread_message_id,
    notification_type,
    request_notification_form_request_id,
  ) => {
    await handleReadDeleteClick(notification_id, "read");

    const type = notification_type?.toLowerCase?.() || "";
    if (type === "removed") {
      return;
    } else if (type === "mute") {
      navigate(buildConversationPath(conversation_id));
    } else if (
      request_notification_form_request_id !== null &&
      request_notification_form_request_id !== ""
    ) {
      window.location.href = `/requests?p=view-request-detail&t=ePR&r=${request_notification_form_request_id}`;
    } else {
      const msgIndex = await ApiService.message.getMessagePosition(
        conversation_id,
        message_id,
      );
      const { success, response } = msgIndex;
      const { index, query } = response;

      if (!success) return showSwal(common.errors.CLICK_NOTIFICATION_ERROR);

      if (
        reply_to_thread_message_id !== "" &&
        reply_to_thread_message_id !== null
      ) {
        navigate(
          buildConversationPath(conversation_id, {
            q: message_id,
            i: index,
            _q: query,
            _t: reply_to_thread_message_id,
            _tr: true,
          }),
        );
      } else {
        navigate(
          buildConversationPath(conversation_id, {
            q: message_id,
            i: index,
            _q: query,
          }),
        );
      }
    }
  };

  return (
    <div
      className={`profile-container notification-container ${addtlClass}`}
      ref={containerRef}
    >
      <button
        type="button"
        className="notification-trigger"
        onClick={handleBellClick}
        aria-label={
          notificationCount > 0
            ? `Notifications, ${notificationCount} unread`
            : "Notifications"
        }
        aria-expanded={showNotifications}
      >
        <img
          src={notificationIcon}
          alt=""
          className="notification-icon"
        />
      </button>

      <span aria-live="polite">
        {notificationCount > 0 && (
          <div className="notification-count-container">
            <div className="notification-count" key={popKey}>
              {notificationCount > 99 ? "99+" : notificationCount}
            </div>
          </div>
        )}
      </span>

      {showNotifications && (
        <div className="notification-list" id="notificationScrollDiv">
          <div className="notification-panel-head">
            <div>
              <h4 className="notification-panel-title">Notifications</h4>
              <small className="notification-panel-subtitle">
                {totalCount} total • {notificationCount} unread
              </small>
            </div>
            <button
              type="button"
              className="notification-markall"
              onClick={handleMarkAllRead}
              disabled={notificationCount === 0}
            >
              Mark all as read
            </button>
          </div>

          <div className="notification-tabs" role="tablist">
            {["all", "unread"].map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={filter === tab}
                className={`notification-tab ${filter === tab ? "active" : ""}`}
                onClick={() => setFilter(tab)}
              >
                {tab === "all" ? "All" : "Unread"}
              </button>
            ))}
          </div>

          {/* Subtle “alive” syncing bar */}
          <div className={`notif-top-status ${isRefreshing ? "show" : ""}`}>
            Syncing…
          </div>

          {loadingFirstPage ? (
            <NotificationSkeletonList rows={7} />
          ) : loadError && notificationList.length === 0 ? (
            <div className="notification-empty-state">
              <span>Couldn't load notifications.</span>
              <button
                type="button"
                className="notification-markall"
                onClick={() => {
                  setPage(1);
                  loadPage(1, "replace");
                }}
              >
                Retry
              </button>
            </div>
          ) : visibleList.length > 0 ? (
            <InfiniteScroll
              dataLength={visibleList.length}
              next={fetchMoreNotifications}
              hasMore={hasMore}
              scrollableTarget="notificationScrollDiv"
              loader={
                <div className="notif-loadmore">
                  <div className="notif-loadmore-spinner" />
                  <span>Loading more notifications…</span>
                </div>
              }
              endMessage={
                <div className="notif-end">
                  <i className="fa fa-check-circle" aria-hidden="true" />
                  <span>You're all caught up</span>
                </div>
              }
            >
              {groupedRows.map((row) => {
                if (row.kind === "header") {
                  return (
                    <div className="notification-day" key={`day-${row.label}`}>
                      {row.label}
                    </div>
                  );
                }

                const notification = row.notification;
                const meta = getNotifMeta(notification.notification_type);
                const sender = notification.notification_sender_details;
                const senderPhoto = sender?.profile_photo
                  ? `${FINAL_RESOURCES_URL}${sender.profile_photo}`
                  : null;
                const preview = notification.message_details?.content
                  ? trimWord(notification.message_details.content, 30)
                  : "";
                const conversationName = notification.conversation_details?.name;

                return (
                  <div
                    className={`notification-item ${
                      notification.is_read ? "" : "notification-item-unread"
                    }`}
                    key={notification.id}
                  >
                    <div
                      className="notification--item"
                      role="button"
                      tabIndex={0}
                      onClick={() => activateNotification(notification)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          activateNotification(notification);
                        }
                      }}
                    >
                      <div className="notification-avatar">
                        {senderPhoto ? (
                          <img
                            src={senderPhoto}
                            alt=""
                            className="notification-user-image"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = emptyImage;
                            }}
                          />
                        ) : (
                          <span className="notification-user-initials">
                            {getInitials(sender?.display_name)}
                          </span>
                        )}
                        <span
                          className={`notification-type-badge notif-tone-${meta.tone}`}
                          aria-hidden="true"
                        >
                          <i className={`fa ${meta.icon}`} />
                        </span>
                      </div>

                      <div className="notification-item-content">
                        <div className="notification-message">
                          {renderNotificationMessage(
                            notification.notification_type,
                            notification.message_details,
                            notification.notification_sender_details,
                            notification.conversation_details,
                          )}
                        </div>

                        {preview && (
                          <div className="notification-preview">{preview}</div>
                        )}

                        <div className="notification-meta">
                          <small
                            className="notification-time"
                            title={convertTo12HourPlusDateFormat(
                              notification.createdAt,
                            )}
                          >
                            {timeAgo(notification.createdAt)}
                          </small>
                          {conversationName && (
                            <span className="notification-chip">
                              {conversationName}
                            </span>
                          )}
                        </div>
                      </div>

                      {!notification.is_read && (
                        <span
                          className="notification-unread-dot"
                          aria-label="Unread"
                        />
                      )}
                    </div>

                    <div className="notification-actions">
                      {!notification.is_read && (
                        <button
                          type="button"
                          className="notification-action-check notif-action"
                          title="Mark as read"
                          aria-label="Mark notification as read"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReadDeleteClick(notification.id, "read");
                          }}
                        >
                          <i className="fa fa-check" aria-hidden="true" />
                        </button>
                      )}

                      <button
                        type="button"
                        className="notification-action-trash notif-action"
                        title="Delete notification"
                        aria-label="Delete notification"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReadDeleteClick(notification.id, "delete");
                        }}
                      >
                        <i className="fa fa-trash-o" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                );
              })}

            </InfiniteScroll>
          ) : (
            <div className="notification-empty-state">
              <i className="fa fa-bell-o notification-empty-icon" aria-hidden="true" />
              <span>
                {filter === "unread"
                  ? "No unread notifications."
                  : "You're all caught up."}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ===========================
   Skeleton Components (UX)
=========================== */
const NotificationSkeletonRow = () => (
  <div className="notif-skel-row">
    <div className="notif-skel-avatar skel" />
    <div className="notif-skel-body">
      <div className="notif-skel-line skel" />
      <div className="notif-skel-line skel mid" />
      <div className="notif-skel-line skel short" />
    </div>
  </div>
);

const NotificationSkeletonList = ({ rows = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <NotificationSkeletonRow key={i} />
    ))}
  </>
);

const renderNotificationMessage = (
  notificationType,
  messageDetails,
  notifSender,
  conversationDetails,
) => {
  const notification_type = notificationType?.toLowerCase?.() || "";

  const senderName = notifSender?.display_name || "Someone";
  const messageContent = messageDetails?.content
    ? (trimWord(messageDetails.content, 15))
    : "";
  const conversationName = conversationDetails?.name || "this conversation";

  switch (notification_type) {
    case "reaction":
      return (
        <span>
          <b>{senderName}</b> reacted to your message{" "}
          {messageContent ? `'${messageContent}'` : "."}
        </span>
      );

    case "mention":
      return (
        <span>
          <b>{senderName}</b> mentioned you in a chat{" "}
          {messageDetails?.content ? (
            <>“{formatBoldText(trimWord(messageDetails.content, 15))}”</>
          ) : (
            "."
          )}
        </span>
      );

    case "thread mention":
      return (
        <span>
          <b>{senderName}</b> mentioned you in a thread reply{" "}
          {messageContent ? `'${messageContent}'` : "."}
        </span>
      );

    case "mute":
      return (
        <span>
          <b>{senderName}</b> muted you in the conversation '{conversationName}
          '.
        </span>
      );

    case "unmute":
      return (
        <span>
          <b>{senderName}</b> unmuted you in the conversation '
          {conversationName}'.
        </span>
      );

    case "removed":
      return (
        <span>
          <b>{senderName}</b> removed you from the conversation '
          {conversationName}'.
        </span>
      );

    case "new form request":
      return (
        <span>
          <b>{senderName}</b> submitted a form request that needs your approval.
        </span>
      );

    case "approve form request":
      return (
        <span>
          <b>{senderName}</b> approved a form request that now needs your
          approval.
        </span>
      );

    case "approved form request":
      return (
        <span>
          <b>{senderName}</b> approved your request. Your request is now{" "}
          <b>APPROVED</b>.
        </span>
      );

    case "deny form request":
      return (
        <span>
          <b>{senderName}</b> denied a form request that was awaiting your
          approval.
        </span>
      );

    case "task follow-up":
      return (
        <span>
          <b>{senderName}</b> followed up on a task.
        </span>
      );

    case "denied form request":
      return (
        <span>
          <b>{senderName}</b> denied your form request.
        </span>
      );

    default:
      return (
        <span>UNSUPPORTED NOTIFICATION TYPE. PLEASE CONTACT SUPPORT.</span>
      );
  }
};

export default NotificationBell;
